import { createSelector } from 'reselect'
import RootState from '../types'
import FiltersState from './types'
import FiltersConfigurationTransport, {
    FilterItemTransport,
    FilterTransport,
} from '../../transports/filters-configuration-transport'
import SortOptionTransport from '../../transports/common/sort-option-transport'
import { BookingTypeEnum } from '../../models/property-details-page/booking-type-model'
import {
    FilterModalType,
    FilterType,
    FilterCollapsedState,
} from '../../components/app-components/filter-sort-section/types'
import orderBy from 'lodash/orderBy'

const MAX_POPULAR_FILTERS = 6

const _getFiltersState = (state: RootState) => state.filters

export const getFiltersConfiguration = createSelector<RootState, FiltersState, FiltersConfigurationTransport | null>(
    _getFiltersState,
    filters => filters.filtersConfigurationResponse,
)

export const getCommittedFiltersConfiguration = createSelector<
    RootState,
    FiltersState,
    FiltersConfigurationTransport | null
>(_getFiltersState, filters => filters.committedFiltersConfigurationResponse)

export const getFilterError = createSelector<RootState, FiltersState, Error | null>(
    _getFiltersState,
    filters => filters.error,
)

export const getIsFilterLoading = createSelector<RootState, FiltersState, boolean>(
    _getFiltersState,
    filters => filters.isLoading,
)

export const getIsFilterBlocking = createSelector<RootState, FiltersState, boolean>(
    _getFiltersState,
    filters => filters.isBlocking,
)

export const getObjectCount = createSelector<RootState, FiltersState, number>(
    _getFiltersState,
    filters => filters.filtersConfigurationResponse?.objectCount ?? 0,
)

export const getRegionNodeId = createSelector<RootState, FiltersState, number>(
    _getFiltersState,
    filters => filters.regionNodeId,
)

export const getCurrentOpenedFilterModal = createSelector<RootState, FiltersState, FilterModalType | undefined>(
    _getFiltersState,
    filters => {
        const stack = filters.modalStack
        return stack?.[stack.length - 1]
    },
)

export const getLastDataPathEncoded = createSelector<RootState, FiltersState, string>(
    _getFiltersState,
    filters => filters.lastDataPathEncoded,
)

export const getIsSpaMode = createSelector<RootState, FiltersState, boolean>(
    _getFiltersState,
    filters => filters.spaMode,
)

export const getSearchRequestId = createSelector<RootState, FiltersState, string | undefined>(
    _getFiltersState,
    filters => filters.searchRequestId,
)

export const getSortOptions = createSelector<RootState, FiltersState, SortOptionTransport[] | null>(
    _getFiltersState,
    filters => filters.filtersConfigurationResponse?.sort || null,
)

export const getInstantBookingFilter = createSelector<RootState, FiltersState, FilterItemTransport | null>(
    _getFiltersState,
    filters => {
        const bookingTypesFilter = filters.filtersConfigurationResponse?.filters?.find(
            filter => filter.type === FilterType.BOOKING_TYPES,
        )

        return bookingTypesFilter?.items?.find(item => item.value === BookingTypeEnum.DIRECT_BOOKING) ?? null
    },
)

export const getActiveFilters = (isBookingBannerActive = true) =>
    createSelector(getFiltersConfiguration, activeFilterConfiguration => {
        if (!activeFilterConfiguration) {
            return []
        }
        return activeFilterConfiguration.filters.flatMap(
            filter =>
                filter.items
                    ?.filter(
                        item =>
                            item.checked &&
                            // Direct booking is surfaced as the InstantBookingBanner toggle, not as a pill
                            !(
                                isBookingBannerActive &&
                                filter.type === FilterType.BOOKING_TYPES &&
                                item.value === BookingTypeEnum.DIRECT_BOOKING
                            ),
                    )
                    .map(item => ({
                        label: item.title?.trim() || filter.title,
                        resetLink: item.encodedLink,
                    })) || [],
        )
    })

export const getPopularFilters = createSelector(getFiltersConfiguration, (filtersConfiguration): FilterTransport => {
    const filters = filtersConfiguration?.filters ?? []
    const filterItems = filters
        .filter(
            filter =>
                filter.type !== FilterType.PRICES &&
                filter.type !== FilterType.CANCELLATION_POLICY &&
                filter.type !== FilterType.POPULAR_FILTERS,
        )
        .flatMap(filter => filter.items ?? [])
        .filter(item => item?.title?.trim())

    const orderedFilterItems = orderBy(filterItems, ['views'], ['desc'])
    const popularFilters =
        orderedFilterItems.length >= MAX_POPULAR_FILTERS
            ? orderedFilterItems.slice(0, MAX_POPULAR_FILTERS)
            : orderedFilterItems

    const directBookingFilterItem = filters
        ?.find(item => item?.type === FilterType.BOOKING_TYPES)
        ?.items?.find(subItem => subItem?.value === BookingTypeEnum.DIRECT_BOOKING)

    const popularFiltersWithDirectBooking = directBookingFilterItem
        ? [directBookingFilterItem, ...popularFilters]
        : popularFilters

    return {
        type: FilterType.POPULAR_FILTERS,
        items: popularFiltersWithDirectBooking,
        itemsActive: '',
        title: '',
        multiple: true,
        moreOrEqual: false,
        isHorizontal: false,
        collapsedState: FilterCollapsedState.UNFOLD,
    }
})
